import { Injectable } from "@angular/core";
import { Actions, CreateEffectMetadata, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, concatMap, map, mergeMap, switchMap, toArray } from "rxjs/operators";
import { LearningPlanActionTypes } from "src/app/constants/StoreConstants";
import { SetSelectedCourseSuccess, SetPanelStateSuccess, UpdatePanelStateSuccess, SetAllowPopUp, SetAllowPopUpSuccess, GetLearningPlan, GetLearningPlanSuccess, GetLearningPlanError, AssignSkillsCheckContentError, AssignSkillsCheckContentSuccess, GetLearnerLearningPlan, GetLearnerLearningPlanSuccess, GetLearnerLearningPlanError, GenerateLearningPlan, EditLearningPlanSuccess, EditLearningPlanError, EditLearningPlan, GetManualLearningPlan, GetManualLearningPlanSuccess, GetManualLearningPlanError, SaveManualLearningPlan, SaveManualLearningPlanSuccess, SaveManualLearningPlanError, UpdateCategoryOnCurrentLevelChange, GetCategoryOnCurrentLevelChangeSuccess } from "./learning-plan.actions";
import { LearningTrackingService } from "src/app/_services/learning-tracking.service";
import { LearningPlanService } from "src/app/_services/learning-plan.service";
import { LearningPlan } from "src/app/_models/LearningPlan";
import { SkillsCheckContent } from "src/app/_models/SkillsCheck";
import { HttpErrorResponse } from "@angular/common/http";
import { LearningPlanEdit, LearningPlanEditByCategoryResult } from "src/app/_models/LearningPlanEdit";
import { ManualLearningPlanResultDto } from "src/app/_models/ManualLearningPlanResultDto";

@Injectable()

export class LearningPlanEffects {

	constructor(
		private action$: Actions,
		private learningTrackingService: LearningTrackingService,
		private learningPlanService: LearningPlanService
	){}

	public setPanelStateSuccess$ = createEffect(():Observable<Action> => {
		return this.action$.pipe(
				ofType(LearningPlanActionTypes.SetPanelState),
				map(() => {
						return SetPanelStateSuccess();
				})				
		)
	});

	public updatePanelStateEffect$ = createEffect(():Observable<Action> => {
			return this.action$.pipe(
					ofType(LearningPlanActionTypes.UpdatePanelState),
					map(() => {
							return UpdatePanelStateSuccess();
					})					
			)
	});

	public selectedCourseEffect$ = createEffect(():Observable<Action> => {
			return this.action$.pipe(
					ofType(LearningPlanActionTypes.SetSelectedCourse),
					map(() => {
							return SetSelectedCourseSuccess();
					})					
			)
	});

	public updateAllowPopUpEffect$ = createEffect((): Observable<Action> => {
		return this.action$.pipe(
			ofType(LearningPlanActionTypes.UpdateAllowPopUpStart),
			map(() => {
				return SetAllowPopUpSuccess();
			})
		)
	});

	public updatePopupReminderEffect$: Observable<Action> & CreateEffectMetadata = createEffect((): Observable<Action> => {
		return this.action$.pipe(
			ofType(LearningPlanActionTypes.UpdatePopUpReminder),
			switchMap((payload: { learnerLearningObjectIds: number[] }) => 
				this.learningTrackingService.updateBulkDateStarted$(payload.learnerLearningObjectIds).pipe(
					map((status: boolean) => {
						return SetAllowPopUp({allowPopUp: status});
					}),
					catchError(()=> {
						return of(SetAllowPopUp({allowPopUp: false}))
					})
				)
			)
		)
	});

	public getLearningPlanEffect$ = createEffect(() => {
		return this.action$.pipe(
			ofType(LearningPlanActionTypes.GetLearningPlan),
			switchMap((payload : {courseId: number}) => 
				this.learningPlanService.getLearningPlanByCourseId$(payload.courseId).pipe(
					map((result: LearningPlan[]) => {
						return GetLearningPlanSuccess({result: result});
					}),
					catchError((error: any) => {
						return of(GetLearningPlanError({error: error}));
					})
				)
			)	
		)
	});
	
	public assignSkillsCheckContent$ = createEffect(():Observable<Action> => {
		return this.action$.pipe(
			ofType(LearningPlanActionTypes.AssignSkillsCheckContent),
			switchMap((payload: {skillsCheckContent: SkillsCheckContent, courseId: number}) => 
				this.learningTrackingService.assignContentForSkillsCheck$(payload.skillsCheckContent).pipe(						
					map(() => {
						return AssignSkillsCheckContentSuccess({courseId: payload.courseId} );
					}),
					catchError((error: any) => {
						return of(AssignSkillsCheckContentError({error: error}));
					})	
				)
			)			
		)
	});

	public assignSkillsCheckContentSuccess$ = createEffect(():Observable<Action> => {
		return this.action$.pipe(
			ofType(AssignSkillsCheckContentSuccess),
			map((payload: {courseId: number}) => {
				return GetLearningPlan({courseId:payload.courseId})
			})
		)
	});

	public getLearnerLearningPlanEffect$ = createEffect((): Observable<Action> => {
		return this.action$.pipe(
			ofType(GetLearnerLearningPlan),
			switchMap((payload: {learnerId: number, courseId: number}) => 
				this.learningPlanService.getLearnerLearningPlan$(payload.learnerId, payload.courseId)
					.pipe(
						map((result: LearningPlan[]) => {
							return GetLearnerLearningPlanSuccess({result: result});
						}),
						catchError((error) => {
							return of(GetLearnerLearningPlanError({error: error}));
						})
					)
			)
		)
	});

	public generateLearningPlanEffect$ = createEffect(() => {
		return this.action$.pipe(
			ofType(GenerateLearningPlan),
			switchMap((payload : { dto: LearningPlanEdit }) => 
				this.learningPlanService.updateFromPreEntry$(payload.dto).pipe(
					map((result: boolean) => {
						return EditLearningPlanSuccess({result: result});
					}),
					catchError((error: HttpErrorResponse) => {
						return of(EditLearningPlanError({error: error}));
					})
				)
			)
		)
	});

	public editLearningPlanEffect$ = createEffect(() => {
		return this.action$.pipe(
			ofType(EditLearningPlan),
			switchMap((payload: { dto: LearningPlanEdit }) => 
				this.learningPlanService.editLearningPlan$(payload.dto).pipe(
					map((result: boolean) => {
						return EditLearningPlanSuccess({result: result});
					}),
					catchError((error: HttpErrorResponse) => {
						return of(EditLearningPlanError({error: error}));
					})
				)
			)
		)
	});

	public getManualLearningPlanEffect$ = createEffect(() => {
		return this.action$.pipe(
			ofType(GetManualLearningPlan),
			switchMap((payload: { courseId: number, learnerId: number }) =>
				this.learningPlanService.getLearnerManualEditLearningPlan$(payload.learnerId, payload.courseId).pipe(
					map((result: ManualLearningPlanResultDto[]) => {
						return GetManualLearningPlanSuccess({ result: result });
					}),
					catchError((error: any) => {
						return of(GetManualLearningPlanError({ error: error }));
					})
				)
			)
		)
	});

	public saveManualLearningPlanEffect$ = createEffect(() => {
		return this.action$.pipe(
			ofType(SaveManualLearningPlan),
			mergeMap((payload) =>{
				return of(...payload.param).pipe(
					concatMap(request => this.learningPlanService.saveLearnerManualEditLearningPlan$(request)),
					toArray(),
					map((result) => {
						if(result.every(isSuccess => isSuccess))
						{
							return SaveManualLearningPlanSuccess({ result: true });
						}
						else
						{
							return SaveManualLearningPlanSuccess({ result: false });
						}
					}),
					catchError((error: any) => {
						return of(SaveManualLearningPlanError({ error: error }));
					})
				);
			})
		)
	});

	public getCategoryOnCurrentLevelChangeEffect$ = createEffect((): Observable<Action> => {
		return this.action$.pipe(
			ofType(UpdateCategoryOnCurrentLevelChange),
			switchMap((payload: {topicId: number, courseId: number, categoryCode: string, level: string, learnerId: number}) => 
				this.learningPlanService.getCategoryOnCurrentLevelChange$(payload.topicId, payload.courseId, payload.categoryCode, payload.level, payload.learnerId)
					.pipe(
						map((result: LearningPlanEditByCategoryResult) => {
							return GetCategoryOnCurrentLevelChangeSuccess({result: result});
						}),
					)
			)
		)
	});
}
